/**
 * Heurísticas de turno: guarda de eco e espera variável de fim de fala.
 *
 * Ficam aqui, fora dos ouvintes, porque os dois precisam delas. O
 * `listenBrowser` usa as duas; o `listenRealtime` usa a guarda de eco mesmo
 * tendo VAD do servidor, porque o VAD ouve o alto-falante tão bem quanto ouve
 * a pessoa e o cancelamento de eco do navegador não é garantia.
 */

import { ESPERA_APOS_CONECTIVO_MS, ESPERA_FIM_DE_TURNO_MS } from './config';

/**
 * Palavras que não terminam frase. Preposições, conjunções e artigos: quem
 * para em "e", "para" ou "com" não terminou, fez pausa. Verbos ficaram de fora
 * de propósito — "eu quero" pode ser uma frase inteira, e alongar a espera
 * dela deixaria a conversa lenta sem ganho.
 */
const CONECTIVOS: ReadonlySet<string> = new Set([
  'e', 'mas', 'então', 'porque', 'aí', 'que', 'para', 'pra', 'com', 'sem',
  'de', 'do', 'da', 'dos', 'das', 'em', 'no', 'na', 'nos', 'nas', 'num', 'numa',
  'ou', 'se', 'quando', 'como', 'tipo', 'sobre', 'entre', 'por', 'pelo', 'pela',
  'até', 'desde', 'após', 'ao', 'aos', 'à', 'às', 'a', 'o', 'os', 'as',
  'um', 'uma', 'uns', 'umas', 'meu', 'minha', 'seu', 'sua', 'esse', 'essa',
  'este', 'esta', 'aquele', 'aquela', 'qual', 'quais', 'onde', 'quem', 'cujo',
  'também', 'além', 'contra', 'perante', 'conforme', 'enquanto', 'embora',
  'portanto', 'porém', 'nem', 'já', 'mais', 'menos', 'muito', 'bem',
]);

/** Minúsculas, sem acento, sem pontuação — a forma em que dois textos se comparam. */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // marcas de acento soltas pelo NFD
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Última palavra em minúsculas e com acento — os conectivos estão acentuados. */
function ultimaPalavra(texto: string): string {
  const limpo = texto.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').trim();
  const partes = limpo.split(/\s+/);
  return partes.length > 0 ? partes[partes.length - 1] : '';
}

export function terminaEmConectivo(texto: string): boolean {
  return CONECTIVOS.has(ultimaPalavra(texto));
}

/** Quanto esperar em silêncio antes de dar o turno por encerrado. */
export function esperaFimDeTurno(texto: string): number {
  return terminaEmConectivo(texto) ? ESPERA_APOS_CONECTIVO_MS : ESPERA_FIM_DE_TURNO_MS;
}

/**
 * A voz do Will voltando pelo microfone.
 *
 * Duas passagens porque o reconhecedor erra: o caminho barato é a contenção
 * literal do trecho ouvido dentro do que está sendo falado; quando o
 * reconhecedor troca uma palavra ("cadastro" por "castro"), a contenção falha
 * e sobra a sobreposição de vocabulário. 60% das palavras longas aparecendo no
 * texto falado é eco — usuário nenhum repete dois terços da frase do
 * assistente enquanto ele ainda a diz.
 *
 * O piso de 3 caracteres evita que "é", "sim" ou "ok" — respostas curtas
 * legítimas e frequentes — sejam engolidas por acaso.
 */
export function ehEco(ouvido: string, falando: string): boolean {
  const o = normalizar(ouvido);
  const f = normalizar(falando);
  if (o.length < 3 || f.length === 0) return false;
  if (f.includes(o)) return true;

  const palavras = o.split(' ').filter((p) => p.length > 3);
  if (palavras.length === 0) return false;
  const acertos = palavras.filter((p) => f.includes(p)).length;
  return acertos / palavras.length >= 0.6;
}
